import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Repeat, Send, Clock, CheckCircle2, AlarmClock } from "lucide-react";
import { toast } from "sonner";

const isOverdue = (d) => new Date(d) <= new Date();

export default function Recall() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["recall"], queryFn: async () => (await api.get("/recall")).data });

  const runDue = useMutation({
    mutationFn: async () => (await api.post("/recall/run-due")).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["recall"] }); toast.success(`${d.sent} recall(s) dispatched via n8n (WhatsApp + email)`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const runOne = useMutation({
    mutationFn: async (id) => (await api.post(`/recall/${id}/run`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["recall"] }); toast.success("Recall reminder sent"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const pending = items.filter((r) => r.status === "pending");
  const due = pending.filter((r) => isOverdue(r.due_at));
  const sent = items.filter((r) => r.status === "sent");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
            <Repeat className="h-7 w-7 text-primary" /> Recall
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Automatic 6-month follow-ups. Completing a booking schedules the next recall.</p>
        </div>
        <Button data-testid="run-due-btn" onClick={() => runDue.mutate()} disabled={!due.length || runDue.isPending} className="gap-2 rounded-full">
          <AlarmClock className="h-4 w-4" /> Run {due.length} due recall{due.length === 1 ? "" : "s"}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="rounded-2xl border-border/60 p-4"><div className="label-eyebrow">Due now</div><div className="mt-1 text-2xl font-bold text-destructive">{due.length}</div></Card>
        <Card className="rounded-2xl border-border/60 p-4"><div className="label-eyebrow">Upcoming</div><div className="mt-1 text-2xl font-bold">{pending.length - due.length}</div></Card>
        <Card className="rounded-2xl border-border/60 p-4"><div className="label-eyebrow">Sent</div><div className="mt-1 text-2xl font-bold text-primary">{sent.length}</div></Card>
      </div>

      <Card className="overflow-hidden rounded-2xl border-border/60">
        {items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground"><Repeat className="mx-auto mb-2 h-8 w-8" /> No recalls scheduled yet.</div>
        ) : (
          <div className="divide-y divide-border/60">
            {items.map((r) => (
              <div key={r.id} data-testid={`recall-${r.id}`} className="flex flex-wrap items-center gap-4 px-5 py-4">
                <div className={`flex h-9 w-9 items-center justify-center rounded-full ${r.status === "sent" ? "bg-primary/10 text-primary" : isOverdue(r.due_at) ? "bg-destructive/10 text-destructive" : "bg-secondary text-muted-foreground"}`}>
                  {r.status === "sent" ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{r.patient_name}</div>
                  <div className="text-xs text-muted-foreground">{r.reason} · due {new Date(r.due_at).toLocaleDateString("en-ZA")}</div>
                </div>
                {r.status === "sent" ? (
                  <Badge variant="secondary" className="rounded-full">Sent · {r.sent_via?.join(", ")}</Badge>
                ) : (
                  <Button size="sm" variant="outline" data-testid={`send-recall-${r.id}`} onClick={() => runOne.mutate(r.id)} className="gap-1 rounded-full">
                    <Send className="h-3.5 w-3.5" /> Send now
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
